/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react'
import { useLocation } from 'react-router'
import { useCheckOneTimePurchase, useConnectApp } from '@Config/fetcher'
import { useRemoteAppActions } from '@Store/slices/remoteApp'
import RouteAwareSkeleton from '@Shared/components/RouteAwareSkeleton'
import AppConnectError from '@Shared/components/AppConnectError'
import getFetcherErrorMessage from '@Utils/fetcherError'
import EmptyLayout from '@Shared/layouts/EmptyLayout'
import useShopDetails from '@Fetcher/hooks/useShopDetails'
import {
  useOnboardingActions,
  useOnboardingState,
} from '@Store/slices/onboarding'
import Onboarding from '@Pages/Onboarding'

function ChargeGuard({ children }: { children: JSX.Element }): JSX.Element {
  const location = useLocation()

  const { setAppUrl } = useRemoteAppActions()

  const { isCompleted: isOnboardingCompleted, isChargeCanceled } =
    useOnboardingState()
  const { setChargeCanceled, setOnboardingStarted, setChargeAccepted } =
    useOnboardingActions()

  const [shopDetailsFetcher, { data: shopDetailsData }] = useShopDetails()
  const [checkOneTimePurchase, { data: oneTimePurchaseData }] =
    useCheckOneTimePurchase()

  const [
    connectApp,
    {
      data: connectAppData,
      isFinished: connectAppIsFinished,
      isInitialized: connectAppIsInitialized,
      isError: connectAppIsError,
      error: connectAppError,
    },
  ] = useConnectApp()

  const queryParams = new URLSearchParams(location.search)
  const chargeId = queryParams.get('charge_id')

  useEffect(() => {
    shopDetailsFetcher()
    if (chargeId) {
      setChargeAccepted(true)
      setOnboardingStarted(false)
      setChargeCanceled(false)
      checkOneTimePurchase(chargeId)
    }
  }, [])

  useEffect(() => {
    if (
      !shopDetailsData ||
      !shopDetailsData.data ||
      (isOnboardingCompleted && isChargeCanceled)
    )
      return

    const updatedFetchURL =
      location.search === ''
        ? `${import.meta.env.VITE_APP_URL}/shopify/payment/${chargeId ? 'process' : 'check'}?shop=${shopDetailsData.data.shop.myshopifyDomain}`
        : `${import.meta.env.VITE_APP_URL}/shopify/payment/${chargeId ? 'process' : 'check'}${location.search}`

    if (
      (chargeId || !oneTimePurchaseData) &&
      !connectAppData &&
      !connectAppIsInitialized
    ) {
      connectApp(updatedFetchURL, chargeId ? 'POST' : 'GET')
    }
  }, [isOnboardingCompleted, oneTimePurchaseData, shopDetailsData])

  if (!isOnboardingCompleted && !chargeId) {
    return <Onboarding />
  }

  if (isOnboardingCompleted && isChargeCanceled) {
    return children
  }

  if (!connectAppIsFinished) {
    return <RouteAwareSkeleton />
  }

  // `useShopifyFetcher` records HTTP failures in `meta.isError` rather than
  // throwing, so a backend failure here would leave `connectAppData` null
  // and fall through to `return children`, bypassing the payment check.
  // Render a recoverable error UI instead.
  if (connectAppIsError) {
    // Show the backend's message when there is one — the billing path throws
    // actionable copy such as "App re-authorization required. Please reinstall
    // the app to continue.", which a generic string would hide.
    const backendMessage = getFetcherErrorMessage(connectAppError)

    return (
      <EmptyLayout>
        <AppConnectError
          {...(backendMessage ? { errorMessage: backendMessage } : {})}
        />
      </EmptyLayout>
    )
  }

  if (connectAppData && !chargeId) {
    const { redirect, url } = connectAppData

    if (redirect) {
      open(url, '_top')
    } else if (url !== '') {
      setAppUrl(url)
    } else {
      return children
    }
  } else {
    return children
  }

  return <></>
}

export default ChargeGuard
